import { DeleteIconOutlined } from '@navi/web-ui/lib/icons';
import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import { Typography } from '@navi/web-ui/lib/primitives';
import axios, { CancelTokenSource } from 'axios';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import CancelIcon from 'src/assets/icons/CancelIcon';
import RetryIconOutline from 'src/assets/icons/RetryIconOutline';
import { SuccessIcon } from 'src/assets/icons/SuccessIcon';
import InfoIconOutlined from 'src/assets/images/icons/InfoIconOutlined';
import CircularProgress from 'src/components/ProgressBars/circularProgress/CircularProgress';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/components/TooltipV2/TooltipV2';
import { v4 as uuidv4 } from 'uuid';
import { uploadAudio, uploadImage } from '../actions';
import styles from '../index.module.scss';
import { FeedbackFormKind, IRenderingEngine } from '../interfaces/index.type';
import AudioPlaceHolder from '@cp/src/assets/icons/AudioPlaceHolder';
import { toast } from '@navi/web-ui/lib/primitives/Toast';

export type imageType = {
  imageData: File;
  progress?: number;
  id: string;
  cancelToken: CancelTokenSource;
  error?: boolean;
  imageUri?: string;
  imageUploadCompleat: boolean;
  incorrectFile: boolean;
  reason?: string;
};
const AudioUpload: React.FC<IRenderingEngine> = props => {
  const {
    question,
    register,
    error,
    toggleLoading,
    interactionType,
    setValue,
    lan,
    customerId,
    source,
    dispatchFeedback,
    unregister,
    clearErrors,
    ...remainingProps
  } = props;
  const { text, referenceId, mandatory, metadata } = question;
  const [selectedFile, setSelectedFile] = useState<Array<imageType>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedImageUri, setSelectedImageUri] = useState<string>();
  const selectedFilesRef = useRef<HTMLInputElement>(null);
  const maxImagesToUpload = metadata?.validators?.max;
  const key = `${source}_${referenceId}`;

  useEffect(() => {
    if (selectedFile.length) {
      let isAllUploadCompleat = true;
      const tempFiles = [];
      for (let i = 0; i < selectedFile.length; i++) {
        tempFiles.push({ tempUri: selectedFile[i].imageUri });
        if (!selectedFile[i].imageUploadCompleat) {
          isAllUploadCompleat = false;
        }
      }
      const finalValue = tempFiles.length > 0 ? JSON.stringify(tempFiles) : '';
      setValue(`${key}`, finalValue, { shouldValidate: true });
      if (isAllUploadCompleat) {
        dispatchFeedback({
          type: FeedbackFormKind.ImageUploadStatus,
          payload: { isImageUpload: false }
        });
      }
    } else {
      setValue(`${key}`, '');
    }
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      setValue(`${key}`, undefined);
      unregister(`${key}`);
      clearErrors(`${key}`);
    };
  }, []);

  const handleFileInputChange = e => {
    preventDefaultAndStopDragging(e);
    const file = e.target.files;
    validateAndUpload(file);

    // upload logic
    // uploadImage(uploadedFiles, setSelectedFile);
  };

  const validateAndUpload = (file: Array<File>) => {
    const uploadedFiles: Array<imageType> = [];
    for (let i = 0; i < file.length; i++) {
      const id = uuidv4();
      const cancelToken = axios.CancelToken.source();
      const error = false;
      if (!file[i].type.includes('audio')) {
        uploadedFiles.push({
          imageData: file[i],
          id,
          cancelToken,
          error: true,
          imageUploadCompleat: false,
          incorrectFile: true,
          reason: 'Invalid file type'
        });
        continue;
      }
      uploadedFiles.push({
        imageData: file[i],
        id,
        cancelToken,
        error,
        imageUploadCompleat: false,
        incorrectFile: false
      });
    }
    if (uploadedFiles.length + selectedFile.length <= maxImagesToUpload) {
      setSelectedFile(prev => [...prev, ...uploadedFiles]);
      uploadAudio(uploadedFiles, setSelectedFile);
      dispatchFeedback({
        type: FeedbackFormKind.ImageUploadStatus,
        payload: { isImageUpload: true }
      });
    } else {
      toast.error(`You can upload only ${maxImagesToUpload} audio(s)`);
    }
  };

  const handleDrop = e => {
    preventDefaultAndStopDragging(e);
    if (selectedFile.length >= maxImagesToUpload) return;
    const files = e.dataTransfer.files;
    const fileArray = Array.from(files);
    validateAndUpload(fileArray as Array<File>);
  };

  const preventDefaultAndStopDragging = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleFileDelete = (index: number) => {
    const files = [...selectedFile];
    files.splice(index, 1);
    setSelectedFile(files);
  };

  const handleView = (url: string) => {
    setSelectedImageUri(url);
  };

  const handleClose = () => {
    setSelectedImageUri(undefined);
  };

  const handleCancel = (id: string) => {
    const cancelToken = selectedFile.find(file => file.id === id)?.cancelToken;
    if (cancelToken) {
      cancelToken.cancel();
      handleFileDelete(selectedFile.findIndex(file => file.id === id));
    }
  };

  return (
    <div
      key={`${key}`}
      className={styles.imageUpload}
      {...register(`${key}`, {
        validate: value => {
          if (mandatory && selectedFile.length === 0) {
            return 'Please add atleast one audio';
          }
          return true;
        }
      })}
    >
      <div className={styles.headingContainer}>
        <Typography variant={'p4'} as={'p'}>
          {text}{' '}
          {mandatory && (
            <sup>
              <RequiredIcon />
            </sup>
          )}
        </Typography>
        <Tooltip placement="top">
          <TooltipTrigger>
            <InfoIconOutlined />
          </TooltipTrigger>
          <TooltipContent className={styles.tooltipContent}>
            <Typography
              as="span"
              color="var(--text-primary)"
              variant="p4"
            >{`Upto ${maxImagesToUpload} screenshots allowed`}</Typography>
          </TooltipContent>
        </Tooltip>
      </div>
      <input
        id="fileUpload"
        type="file"
        accept="audio/*"
        className={styles.fileInput}
        multiple
        ref={selectedFilesRef}
        disabled={selectedFile.length >= maxImagesToUpload}
        onChange={handleFileInputChange}
      />
      <label htmlFor="fileUpload">
        <div
          className={cx(styles.dropzone, { ['!border-red-400']: error?.[`${key}`] })}
          style={{ opacity: selectedFile.length >= maxImagesToUpload ? 0.5 : 1 }}
          onDragOver={preventDefaultAndStopDragging}
          onDragEnter={preventDefaultAndStopDragging}
          onDrop={handleDrop}
          onDragExit={() => setIsDragging(false)}
          onDragEnd={() => setIsDragging(false)}
          onDragExitCapture={() => setIsDragging(false)}
        >
          <AudioPlaceHolder className={styles.icon} />
          <div className={styles.textContainer}>
            <Typography variant="h5">
              <span className={styles.blueText}>Click to upload</span>{' '}
              <span className={styles.blueText}>or drag and drop here</span>
            </Typography>
          </div>
        </div>
      </label>
      {error?.[`${key}`] && (
        <Typography variant="p5" className={cx(styles.errorText, 'mt-2')}>
          Please {text}
        </Typography>
      )}
      {selectedFile.length ? (
        <div className={styles.imageContainer}>
          {selectedFile.map((file, index) => (
            <div key={file.id} className={styles.imageItem}>
              <div className={styles.imageItemContainer}>
                <Typography
                  className={cx(styles.mediaFileName, { [styles.errorText]: file.error })}
                  variant={'p3'}
                  as={'p'}
                >
                  <span className="pl-3">{file.imageData.name}</span>
                </Typography>
              </div>
              <div
                className={styles.iconContainer}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {file.reason && (
                  <Tooltip placement="top">
                    <TooltipTrigger>
                      <InfoIconOutlined fillColor="#e92c2c" />
                    </TooltipTrigger>
                    <TooltipContent className={styles.tooltipContent}>
                      <Typography as="span" color="var(--text-primary)" variant="p4">
                        {file.reason}
                      </Typography>
                    </TooltipContent>
                  </Tooltip>
                )}
                {file.error ? (
                  <RetryIconOutline
                    onClick={() => uploadImage([file], setSelectedFile)}
                    className={styles.icon}
                  />
                ) : file.imageUploadCompleat ? (
                  <SuccessIcon className={styles.successIcon} />
                ) : (
                  <CircularProgress
                    percentage={file?.progress ?? 0}
                    sqSize={20}
                    strokeWidth={3}
                    withAnimation={false}
                    circleBackground="var(--navi-color-grey-c3)"
                  />
                )}
                {file?.progress === 100 || file.error ? (
                  <DeleteIconOutlined
                    onClick={() => handleFileDelete(index)}
                    className={styles.icon}
                  />
                ) : (
                  <CancelIcon onClick={() => handleCancel(file.id)} className={styles.icon} />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AudioUpload;
